import _ from 'lodash';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { BasePage, PageType } from 'src/components/BasePage';
import { FormioForm } from 'src/components/FormioForm/FormioForm';
import { FormioCustomEvent, FormioEvent } from 'src/core/Formio.types';
import { EntityLinks } from 'src/core/router/EntityLinks';
import { Router } from 'src/core/router/Router';
import { TaskViewHeader } from 'src/pages/TasksPage/TaskViewHeader';
import { TaskViewViewModel } from 'src/pages/TasksPage/TaskViewViewModel';
import { CurrentTaskStore } from 'src/stores/CurrentTaskStore/CurrentTaskStore';
import styled from 'styled-components';

type TaskViewRouterParams = {
    taskId: string;
};

export const TaskView = observer(() => {
    const { taskId } = useParams<TaskViewRouterParams>();
    const isReadOnly = !TaskViewViewModel.taskDefinition?.assignee;

    useEffect(() => {
        TaskViewViewModel.loadItem(taskId);

        return () => {
            TaskViewViewModel.reset();
        };
    }, [taskId]);

    const submitHandler = useMemo(() => {
        return getSubmitHandler(taskId);
    }, [taskId]);

    const { isLoading } = TaskViewViewModel.loadingStatus;

    return (
        <StyledPage pageName="task" isLoading={isLoading} type={PageType.FULL}>
            {taskId ? (
                <React.Fragment key={isReadOnly?.toString()}>
                    <TaskViewHeader />
                    <FormioForm
                        form={TaskViewViewModel.taskForm as any}
                        submission={TaskViewViewModel.taskVariables}
                        onSubmit={submitHandler as any}
                        onCustomEvent={getCustomEventHandler(taskId) as any}
                        readOnly={isReadOnly}
                        normalizeSubmission
                    />
                </React.Fragment>
            ) : null}
        </StyledPage>
    );
});

const StyledPage = styled(BasePage)`
    .formio-component-submit1 {
        button {
            margin-left: 1.6rem;
        }
    }
`;

const getSubmitHandler = (taskId: string) => {
    let isSubmitting = false;

    return async (values: FormioEvent) => {
        if (isSubmitting) {
            return;
        }
        isSubmitting = true;

        const { data } = values;
        await submitForm(taskId, data);
    };
};

const submitForm = async (taskId: string, data: any) => {
    const tasks = await TaskViewViewModel.submitForm(taskId, data);
    CurrentTaskStore.reset();
    if (tasks?.length) {
        const firstTask = tasks[0];
        Router.navigate(EntityLinks.tasks.itemDetails(firstTask.id));
    } else {
        Router.navigate(EntityLinks.tasks.list());
    }
};

const getCustomEventHandler =
    (processKey: string) =>
    async (event: FormioCustomEvent<'submitWithoutData'>) => {
        if (event.type === 'submitWithoutData') {
            const data = _.pick(event.data, [event.component.key]);
            await submitForm(processKey, data);
        }
    };
